import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormFillFields, { FormLocaleSwitch } from './FormFillFields'
import FormBrandingHeader, { FormTenantLogo, formSubmitButtonStyle } from './FormBrandingHeader'
import {
  DEFAULT_LOCALE,
  afterSubmitRedirect,
  formCardMaxWidth,
  formPageBackgroundIsDark,
  formPageStyle,
  normalizeLocales,
  resolveConfirmMessage,
  shouldShowTenantLogo,
  shouldUseClubColors,
} from './formBuilderModel'

export default function FormAssignmentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [bundle, setBundle] = useState(null)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const [locale, setLocale] = useState(DEFAULT_LOCALE)

  const load = useCallback(async () => {
    setError('')
    const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}`))
    const j = await res.json()
    if (!res.ok || !j.success) {
      setError(j.error || 'Failed to load assignment')
      return
    }
    setBundle(j.data)
    const locales = normalizeLocales(j.data.version?.settings)
    setLocale(locales[0] || DEFAULT_LOCALE)
    const existing = j.data.submission?.answers || j.data.draft?.answers || {}
    const { schemaVersion, ...rest } = existing
    setAnswers(rest)
  }, [id])

  useEffect(() => { load() }, [load])

  const readOnly = ['accepted', 'cancelled'].includes(bundle?.assignment?.status) || !!bundle?.submission

  const onChange = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))

  const saveDraft = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/draft`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Draft failed')
      setInfo('Draft saved')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    setErrors({})
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      })
      const j = await res.json()
      if (res.status === 422 && j.errors) {
        setErrors(Object.fromEntries(j.errors.map((e) => [e.fieldId, e.message])))
        throw new Error(j.error || 'Please fix the highlighted fields')
      }
      if (!res.ok || !j.success) throw new Error(j.error || 'Submit failed')
      const nextRef = j.data.submission?.ref || ''
      const dest = afterSubmitRedirect(bundle?.version?.settings, nextRef)
      setInfo(resolveConfirmMessage(bundle?.version?.settings, {
        locale,
        answers,
        definition: bundle?.version?.definition,
        formName: bundle?.assignment?.formName,
        ref: nextRef,
      }) + (nextRef ? ` — ${nextRef}` : ''))
      if (dest) window.setTimeout(() => { window.location.assign(dest) }, 1600)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const act = async (action) => {
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/${action}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || `${action} failed`)
      if (j.formUrl) setInfo(j.formUrl)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!bundle && !error) return <div className="p-6 text-sm text-slate-500">Loading…</div>
  const a = bundle?.assignment
  const settings = bundle?.version?.settings
  const branding = bundle?.branding
  const showBranding = shouldShowTenantLogo(settings) || shouldUseClubColors(settings)
  const darkPage = !!(bundle?.version && formPageBackgroundIsDark(settings, branding))
  const cardMax = formCardMaxWidth(settings)
  const navLinkCls = darkPage
    ? 'text-sm text-teal-200 hover:text-white'
    : 'text-sm text-teal-700 hover:text-teal-900'
  const metaCls = darkPage ? 'text-sm text-slate-200 mt-1' : 'text-sm text-slate-500 mt-1'
  const testBadge = a?.isTest ? (
    <span className="ml-2 align-middle rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      Test
    </span>
  ) : null

  return (
    <div className="p-6 min-h-full" style={bundle?.version ? formPageStyle(settings, branding) : undefined}>
      <div className="mx-auto" style={{ maxWidth: cardMax, width: '100%' }}>
      <div className="flex flex-wrap gap-3 mb-3">
        <button type="button" className={navLinkCls} onClick={() => navigate('/compliance-safe/forms/pipeline')}>← Pipeline</button>
        {a?.formId ? (
          <button type="button" className={navLinkCls} onClick={() => navigate(`/compliance-safe/forms/${a.formId}/records`)}>
            All records for this form
          </button>
        ) : null}
      </div>
      {a ? (
        <div className="mb-3">
          {shouldShowTenantLogo(settings) ? (
            <FormTenantLogo settings={settings} branding={branding} darkPage={darkPage} />
          ) : null}
          <p className={metaCls}>
            {a.status} · {a.facing} · {a.subjectLabel || a.subjectType}
            {a.submissionRef ? ` · ${a.submissionRef}` : ''}
          </p>
          {a.isTest ? (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Test run — fill and submit as you would live. The record appears on Form records and the pipeline with a Test badge.
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {info ? <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800 break-all">{info}</div> : null}
      {bundle?.version ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <FormBrandingHeader
            formName={a?.formName}
            titleExtra={testBadge}
          />
          <FormLocaleSwitch settings={bundle.version.settings} locale={locale} onChange={setLocale} />
          <FormFillFields
            definition={bundle.version.definition}
            settings={bundle.version.settings}
            locale={locale}
            answers={answers}
            onChange={onChange}
            errors={errors}
            readOnly={readOnly}
          />
          <div className="flex flex-wrap gap-2 mt-6">
            {!readOnly ? (
              <>
                <Button variant="outline" onClick={saveDraft} disabled={busy}>Save draft</Button>
                <Button onClick={submit} disabled={busy} style={showBranding ? formSubmitButtonStyle(settings, branding) : undefined}>
                  Submit
                </Button>
              </>
            ) : null}
            {a?.status === 'in_review' ? <Button onClick={() => act('accept')} disabled={busy}>Accept</Button> : null}
            {a?.assigneeRoleId && !a?.claimedBy ? <Button variant="outline" onClick={() => act('claim')} disabled={busy}>Claim</Button> : null}
            {a?.facing === 'patron' && ['sent', 'opened', 'in_progress', 'overdue'].includes(a.status) ? (
              <Button variant="outline" onClick={() => act('remind')} disabled={busy}>Remind</Button>
            ) : null}
            {!['accepted', 'cancelled'].includes(a?.status) ? (
              <Button variant="outline" onClick={() => act('cancel')} disabled={busy}>Cancel</Button>
            ) : null}
          </div>
        </div>
      ) : null}
      </div>
    </div>
  )
}
